import React, { createContext, useContext, useEffect, useState } from "react";
import {
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User,
} from "firebase/auth";
import { auth } from "./firebaseConfig";

interface AuthContextType {
    user: any;
    userType: string | null;
    passwordChange: (password: string, newPassword: string) => Promise<void>;
    signInUser: (email: string, password: string) => Promise<void>;
    signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [userType, setUserType] = useState<string | null>("");

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                setUser(null);
                setUserType(null);
                return;
            }

            const idTokenResult = await currentUser!.getIdTokenResult(true);
            const type = idTokenResult.claims.type;

            if (type !== "ADMIN" && type !== "SUPER_ADMIN") {
                signOut(auth);
                window.location.href = "/";
            }

            setUserType(idTokenResult.claims.type as string);
            setUser(currentUser);
        });

        return () => unsubscribe();
    }, []);

    // 로그인 함수
    const signInUser = async (email: string, password: string) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error: any) {
            throw new Error(error.code);
        }
    };

    const passwordChange = async (password: string, newPassword: string) => {
        try {
            // 재인증
            const credential = EmailAuthProvider.credential(user!.email!, password);
            await reauthenticateWithCredential(user as User, credential);
            // 비밀번호 변경
            await updatePassword(user as User, newPassword);
        } catch (error: any) {
            throw new Error(error.code);
        }
    };

    // 로그아웃 함수
    const signOutUser = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setUserType(null);
        } catch (error: any) {
            throw new Error(error.code);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                userType,
                signInUser,
                passwordChange,
                signOutUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
